import { ChangeDetectorRef, Injectable, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { I18nPluralPipe, NgLocalization } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Maps a numeric value to a pluralized translated string.
 *
 * ## Usage
 *
 * expression | pluralate:'PATH.TO.TRANSLATION.MAPPING'
 *
 * ## Example
 *
 * ```ts
 * @Component({
 *   selector: 'my-inbox',
 *   template: `
 *     <div>
 *       {{ messages.length | pluralate:'INBOX.MESSAGES_PLURAL_CATEGORIES' }}
 *     </div>
 *   `,
 * })
 * class MyInbox {
 *   messages: any[];
 *   // ...
 * }
 * ```
 *
 * While the translation file is:
 *
 * ```json
 * // en.json
 * {
 *  "INBOX": {
 *    "INBOX.MESSAGES_PLURAL_ZERO': 'Your inbox is empty :(',
 *    'INBOX.MESSAGES_PLURAL_ONE': 'You have 1 new message',
 *    'INBOX.MESSAGES_PLURAL_OTHER': 'You have {{ count }} new messages',
 *
 *    'MESSAGES_PLURAL_CATEGORIES': {
 *      '=0': 'INBOX.MESSAGES_PLURAL_ZERO',
 *      '=1': 'INBOX.MESSAGES_PLURAL_ONE',
 *      'other': 'INBOX.MESSAGES_PLURAL_OTHER'
 *    }
 *  }
 * }
 * ```
 *
 * @implements {PipeTransform}
 */
@Injectable({
    providedIn: 'root'
})
@Pipe({
  standalone: true,
    name: 'pluralate',
    pure: false, // required to update the value when the promise is resolved
})
export class PluralatePipe implements PipeTransform, OnDestroy {

    private value = '';
    private subscriptions: Subscription[] = [];

    constructor(
        private translate: TranslateService,
        private changeDetector: ChangeDetectorRef,
        private localization: NgLocalization
    ) {
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public transform(query: any, pluralizationHolderKey: string): string {
        const i18nPipe: I18nPluralPipe = new I18nPluralPipe(this.localization);

        if (!query || query.length === 0) {
            return query;
        }

        const queryNum = parseInt(query, 10);
        if (isNaN(queryNum)) {
            return query;
        }

        this.subscriptions.push(
            this.translate.get(pluralizationHolderKey).subscribe((res: { [key: string]: string }) => {
                const pluralCat = i18nPipe.transform(queryNum, res);
                this.value = this.translate.instant(pluralCat, { count: queryNum });
            })
        );

        return this.value;
    }
}
